import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Trash2, UserPlus, Phone, Mail, Building2, FileText, AlertTriangle, MapPin, Printer } from "lucide-react";
import { api, formatApiError, BACKEND_URL, TOKEN_KEY } from "@/lib/api";

async function downloadFile(path, filename) {
  const token = localStorage.getItem(TOKEN_KEY);
  const resp = await fetch(`${BACKEND_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

const PRODUCT_OPTIONS = [
  { value: "stretch_film", label: "Stretch Film" },
  { value: "bopp_tape", label: "BOPP Self-Adhesive Tape" },
  { value: "both", label: "Both" },
];

const STATUS_COLORS = {
  new: "bg-orange-100 text-orange-800 border-orange-300",
  contacted: "bg-zinc-200 text-zinc-700 border-zinc-300",
  converted: "bg-green-100 text-green-800 border-green-300",
  lost: "bg-red-100 text-red-700 border-red-300",
};

const STATUS_LABEL = { new: "New", contacted: "Contacted", converted: "Converted", lost: "Lost" };

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/inquiries").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const visible = items.filter((it) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return [it.contact_name, it.company, it.phone, it.gst, it.email].some((v) => (v || "").toLowerCase().includes(f));
  });

  const convert = async (it) => {
    if (!window.confirm(`Convert "${it.contact_name}${it.company ? ` (${it.company})` : ""}" to a Customer?`)) return;
    try {
      await api.post(`/inquiries/${it.id}/convert`);
      toast.success("Converted — now in Customer list");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete inquiry "${it.contact_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/inquiries/${it.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const setStatus = async (it, status) => {
    try {
      await api.put(`/inquiries/${it.id}`, { ...it, status });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const productLabel = (k) => PRODUCT_OPTIONS.find((p) => p.value === k)?.label || k;

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Sales · Leads</div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">New Inquiry</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="export-inquiries-pdf-btn"
            disabled={items.length === 0}
            onClick={async () => {
              try {
                const today = new Date().toISOString().slice(0, 10);
                await downloadFile("/api/inquiries/export/pdf", `inquiries-${today}.pdf`);
              } catch (e) { toast.error("Could not download PDF"); }
            }}
            className="border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-800 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2 disabled:opacity-40"
            title="Print the inquiries pipeline as a PDF"
          ><Printer size={14} /> Export PDF</button>
          <button
            data-testid="export-inquiries-xlsx-btn"
            disabled={items.length === 0}
            onClick={async () => {
              try {
                const today = new Date().toISOString().slice(0, 10);
                await downloadFile("/api/inquiries/export/xlsx", `inquiries-${today}.xlsx`);
              } catch (e) { toast.error("Could not download XLS"); }
            }}
            className="border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-800 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2 disabled:opacity-40"
            title="Download the inquiries pipeline as XLS"
          ><Printer size={14} /> Export XLS</button>
          <button
            data-testid="new-inquiry-btn"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2"
          ><Plus size={16} /> New Inquiry</button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input
          data-testid="inquiry-filter"
          placeholder="Search name / company / phone / GST / email"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full max-w-md h-9 px-3 text-sm border border-zinc-300 bg-white font-mono"
        />
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{visible.length} of {items.length}</div>
      </div>

      <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="inquiries-table">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Contact</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Company</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Phone</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">GST</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Product Interest</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Status</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Date</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Action</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {visible.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-zinc-500 py-12 text-sm font-sans">No inquiries yet. Click "New Inquiry" to capture your first lead.</td></tr>
            ) : visible.map((it) => {
              const isDup = (it.duplicate_of || []).length > 0;
              const isConverted = it.status === "converted";
              return (
                <tr key={it.id} className={`border-b border-zinc-200 hover:bg-zinc-50 ${isDup ? "bg-amber-50/40" : ""}`} data-testid={`inq-row-${it.id}`}>
                  <td className="px-3 py-2 font-semibold text-zinc-950">
                    <div className="flex items-center gap-2">
                      {it.contact_name}
                      {isDup && (
                        <span
                          data-testid={`inq-dup-${it.id}`}
                          title={`Possible duplicate of ${it.duplicate_of.length} other ${it.duplicate_of.length === 1 ? "entry" : "entries"} (same phone / GST / name)`}
                          className="inline-flex items-center gap-1 text-[9px] uppercase font-mono border border-amber-400 bg-amber-100 text-amber-800 px-1.5 py-0.5"
                        ><AlertTriangle size={9} /> Possible Duplicate</span>
                      )}
                    </div>
                    {it.email && <div className="text-[10px] text-zinc-500 font-mono">{it.email}</div>}
                  </td>
                  <td className="px-3 py-2">{it.company || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-3 py-2">{it.phone || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-3 py-2 text-[11px]">{it.gst || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-3 py-2 text-[11px]">{productLabel(it.product_interest)}</td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`inq-status-${it.id}`}
                      value={it.status}
                      disabled={isConverted}
                      onChange={(e) => setStatus(it, e.target.value)}
                      className={`text-[10px] font-mono uppercase border px-2 py-0.5 disabled:opacity-60 ${STATUS_COLORS[it.status]}`}
                    >
                      {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-zinc-600">{(it.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {!isConverted && (
                      <button
                        data-testid={`inq-convert-${it.id}`}
                        onClick={() => convert(it)}
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-orange-400 bg-orange-50 text-orange-800 px-2 py-1 hover:bg-orange-100 mr-1"
                        title="Convert this inquiry to a Customer"
                      ><UserPlus size={11} /> Convert</button>
                    )}
                    <button
                      data-testid={`inq-edit-${it.id}`}
                      onClick={() => { setEditing(it); setShowForm(true); }}
                      className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 mr-1"
                    >Edit</button>
                    <button
                      data-testid={`inq-delete-${it.id}`}
                      onClick={() => remove(it)}
                      className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-zinc-300 text-red-700 px-2 py-1 hover:bg-red-50"
                    ><Trash2 size={11} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <InquiryForm
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function InquiryForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    contact_name: "", company: "", phone: "", email: "", gst: "", address: "",
    product_interest: "stretch_film", notes: "", status: "new",
  });
  const [busy, setBusy] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name?.trim()) { toast.error("Contact name is required"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/inquiries/${editing.id}`, form);
      else await api.post("/inquiries", form);
      toast.success(editing ? "Updated" : "Inquiry saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cls = "w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono focus:outline-none focus:border-orange-600";
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white border border-zinc-300 w-full max-w-lg" data-testid="inquiry-form">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{editing ? "Edit Inquiry" : "Capture New Lead"}</div>
            <h2 className="text-lg font-semibold">{editing ? editing.contact_name : "New Inquiry"}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block">Contact Name *</label>
            <input data-testid="field-contact-name" value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} className={cls} required autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block flex items-center gap-1"><Building2 size={10} /> Company</label>
              <input data-testid="field-company" value={form.company} onChange={(e) => setField("company", e.target.value)} className={cls} />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block flex items-center gap-1"><Phone size={10} /> Phone</label>
              <input data-testid="field-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={cls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block flex items-center gap-1"><Mail size={10} /> Email</label>
              <input data-testid="field-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={cls} />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">GST (optional)</label>
              <input data-testid="field-gst" value={form.gst} onChange={(e) => setField("gst", e.target.value)} className={cls} />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block flex items-center gap-1"><MapPin size={10} /> Address (optional)</label>
            <textarea
              data-testid="field-address"
              rows={2}
              value={form.address || ""}
              onChange={(e) => setField("address", e.target.value)}
              className="w-full px-2 py-2 text-sm border border-zinc-300 bg-white font-mono focus:outline-none focus:border-orange-600"
              placeholder="Street, City, State, PIN"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase mb-2 block">Product Interest</label>
            <div className="grid grid-cols-3 gap-2">
              {PRODUCT_OPTIONS.map((p) => (
                <button
                  key={p.value}
                  data-testid={`product-${p.value}`}
                  type="button"
                  onClick={() => setField("product_interest", p.value)}
                  className={`text-[11px] uppercase font-mono tracking-wider px-3 py-2 border ${form.product_interest === p.value ? "bg-orange-600 border-orange-600 text-white" : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}
                >{p.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block flex items-center gap-1"><FileText size={10} /> Notes (optional)</label>
            <textarea data-testid="field-notes" rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="w-full px-2 py-2 text-sm border border-zinc-300 bg-white font-mono focus:outline-none focus:border-orange-600" placeholder="e.g. Wants samples, 500 ctns / month, etc." />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2 bg-zinc-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-mono border border-zinc-300 hover:bg-zinc-100">Cancel</button>
          <button type="submit" disabled={busy} data-testid="save-inquiry-btn" className="px-4 py-2 text-xs uppercase tracking-wider font-mono bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
